import type { PoiFilterId$options } from '$houdini';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { PoiCardSize } from '../card/types';
import type {
	AccommodationMichelinPoiData,
	AccommodationPoiData,
	GasStationPoiData,
	ParkingPoiData,
	Partner1PoiData,
	Partner2PoiData,
	PoiCategory,
	PoiData,
	PoiFilterId,
	PoiFilterOptionId,
	PoiFilterValue,
	PoiType,
	RestaurantMichelinPoiData,
	RestaurantPoiData,
	RestStopPoiData,
	ServiceEVPoiData,
	ServicePoiData,
	TourismPoiData,
} from '../types';
import { isNotFalsy } from '$lib/utils/filter';
import { getFuelsAndGasStationFiltersAndLastUpdated } from '../card/factories-utils';

const servicesCategoryFilterMapping = {
	EV_SERVICE: ['CONNECTOR', 'POWER'],
	SERVICE_GAS_STATION: [
		'GAS_STATION_NETWORK',
		'GAS_STATION_EQUIPMENT',
		'GAS_STATION_LAST_UPDATE',
		'GAS_STATION_TYPE',
	],
	REST_STOP: ['FAMILY_FRIENDLY_REST_STOP'],
};

type GetCardHeight<T extends PoiData> = (poidata: T, height: number) => number;

const inRange = function (value: number, min: number, max: number): boolean {
	return value >= min && value <= max;
};

const getEVCardHeight: GetCardHeight<ServiceEVPoiData> = function (
	poidata: ServiceEVPoiData,
	height: number,
): number {
	const { payment_methods, connectors } = poidata;
	const free = payment_methods.length === 0;
	const connectorsCount = connectors.length;

	if (
		((connectorsCount === 0 || connectorsCount === 1) && free)
		|| (connectorsCount === 0 && !free)
	) {
		return height;
	}

	if ((connectorsCount === 1 && !free) || connectorsCount === 2) {
		height += 1;
	} else if (connectorsCount === 3 && free) {
		height += 2;
	} else if ((connectorsCount === 3 && !free) || connectorsCount > 3) {
		height += 3;
	}
	return height;
};

const getAccommodationCardHeight: GetCardHeight<AccommodationPoiData> = function (
	poidata: AccommodationPoiData,
	height: number,
): number {
	return height;
};

const getAccommodationMichelinCardHeight: GetCardHeight<AccommodationMichelinPoiData> = function (
	poidata: AccommodationMichelinPoiData,
	height: number,
): number {
	return height;
};

const getParkingCardHeight: GetCardHeight<ParkingPoiData> = function (
	poidata: ParkingPoiData,
	height: number,
): number {
	return height;
};

const getPartnerCardHeight: GetCardHeight<Partner1PoiData> = function (
	poidata: Partner1PoiData,
	height: number,
): number {
	return height;
};

const getPartner2CardHeight: GetCardHeight<Partner2PoiData> = function (
	poidata: Partner2PoiData,
	height: number,
): number {
	return height;
};

const getServiceRestStopCardHeight: GetCardHeight<RestStopPoiData> = function (
	poidata: RestStopPoiData,
	height: number,
): number {
	if (poidata.isFamilyFriendly) {
		height += 1;
	}
	return height;
};

const getRestaurantMichelinCardHeight: GetCardHeight<RestaurantMichelinPoiData> = function (
	poidata: RestaurantMichelinPoiData,
	height: number,
): number {
	return height;
};

const getRestaurantCardHeight: GetCardHeight<RestaurantPoiData> = function (
	poidata: RestaurantPoiData,
	height: number,
): number {
	return height;
};

const getGasStationCardHeight: GetCardHeight<GasStationPoiData> = function (
	poidata: GasStationPoiData,
	height: number,
): number {
	const { fuelPricesMap, gasStationTypeFilter } = getFuelsAndGasStationFiltersAndLastUpdated(poidata);
	const fuelPrices = gasStationTypeFilter
		? fuelPricesMap.filter(fuel => (gasStationTypeFilter as PoiFilterOptionId[]).includes(fuel.filterOptionId as PoiFilterOptionId))
		: fuelPricesMap;
	const pricesArrayLength = fuelPrices.length;
	const hiddenLength = gasStationTypeFilter !== undefined ? fuelPricesMap.length - fuelPrices.length : undefined;

	let gasStationCardHeight = height;

	if (!pricesArrayLength) {
		return gasStationCardHeight;
	}
	if (pricesArrayLength <= 2) {
		const add = hiddenLength ? 2 : 1;
		gasStationCardHeight += add;
	}
	if (inRange(pricesArrayLength, 3, 4)) {
		const add = hiddenLength ? 3 : 2;
		gasStationCardHeight += add;
	}
	if (pricesArrayLength >= 5) {
		gasStationCardHeight += 3;
	}

	return gasStationCardHeight;
};

const getTourismPoiCardHeight: GetCardHeight<TourismPoiData> = function (
	poidata: TourismPoiData,
	height: number,
): number {
	return height;
};

const getServiceCardHeight: GetCardHeight<ServicePoiData> = function (
	poidata: ServicePoiData,
	height: number,
): number {
	return height;
};

const getCardHeightByPoiType: {
	[K in PoiType]: GetCardHeight<Extract<PoiData, { __typename: K }>>;
} = {
	ServiceEVPOI: getEVCardHeight,
	AccommodationPOI: getAccommodationCardHeight,
	AccommodationMichelinPOI: getAccommodationMichelinCardHeight,
	RestaurantPOI: getRestaurantCardHeight,
	RestaurantMichelinPOI: getRestaurantMichelinCardHeight,
	ParkingPOI: getParkingCardHeight,
	ServiceGasStationPOI: getGasStationCardHeight,
	TourismPOI: getTourismPoiCardHeight,
	ServicePOI: getServiceCardHeight,
	ServiceRestStopPOI: getServiceRestStopCardHeight,
	PartnerPOI: getPartnerCardHeight,
	Partner2POI: getPartner2CardHeight,
};

export function getCardHeight(poiData: PoiData): PoiCardSize {
	// Possible card sizes :
	// xs / 32 units / 120px
	// sm / 50 units / 192px
	// md / 65 units / 252px
	// lg / 70 units / 272px
	// xl / 79 units / 308px
	const cardSizeList: PoiCardSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

	// 'sm' is the minimal starting point of all poi card with images
	// Tourism, AccommodationMichelin and RestaurantMichelin starts at 'md' because the mention arg is always present
	// Parking card will always be 'xs'
	// GasStation card's size depends of the number of fuel types
	const mapTypeToStartIndex: Record<PoiType, number> = {
		AccommodationPOI: 3,
		AccommodationMichelinPOI: 3,
		ParkingPOI: 0,
		ServiceRestStopPOI: 0,
		RestaurantMichelinPOI: 3,
		RestaurantPOI: 3,
		TourismPOI: 3,
		ServicePOI: 0,
		ServiceGasStationPOI: 0,
		ServiceEVPOI: 1,
		PartnerPOI: 0,
		Partner2POI: 0,
	};

	const height = mapTypeToStartIndex[poiData.__typename];
	// It goes up a category if an info field is present

	const getHeightForACard = getCardHeightByPoiType[poiData.__typename] as GetCardHeight<PoiData>;
	const heightForACard = getHeightForACard(poiData, height);

	if (heightForACard >= 0 && heightForACard < cardSizeList.length) {
		return cardSizeList[heightForACard];
	}
	throw new Error('Statement should be unreachable');
}

function isSegmentInPoiTypes(segment: string, poiCategory: PoiCategory) {
	// NOTE: When filter is ONLY_MICHELIN_GUIDE_SELECTION and poiCategories has 'RESTAURANT' or 'TOURISM' then the segment is in PoiTypes
	return (poiCategory === segment) || (segment === 'SELECTION' && (poiCategory === 'RESTAURANT' || poiCategory === 'TOURISM'));
}

function filterKeyIncludesPoiCategoryName(filterKey: string, poiCategory: PoiCategory) {
	return filterKey.split('_').some(segment => isSegmentInPoiTypes(segment, poiCategory));
}

function hasCorrespondingPoiCategoryForFilter(
	category: PoiCategory,
	filter: PoiFilterId,
) {
	if (category === 'SERVICE_GAS_STATION' || category === 'EV_SERVICE' || category === 'REST_STOP') {
		return servicesCategoryFilterMapping[category].includes(filter);
	}
	return filterKeyIncludesPoiCategoryName(filter, category);
}

export function isPoiCategoryInFilterKey(filters: PoiFilterId[], poiCategories: PoiCategory[]) {
	return filters.some(filter =>
		poiCategories.some(category =>
			hasCorrespondingPoiCategoryForFilter(category, filter),
		),
	);
}

function extractSelectedFilterValues(
	poiFilterId: PoiFilterId,
	poiFilterValue: PoiFilterValue,
	poiCategories: PoiCategory[],
) {
	return poiCategories.map(category =>
		(hasCorrespondingPoiCategoryForFilter(category, poiFilterId) && poiFilterValue) || undefined,
	).filter(isNotFalsy);
}
export function getSelectedPoiCategoryFilters(filters: Partial<Record<PoiFilterId$options, PoiFilterValue>>, poiCategories: PoiCategory[]) {
	return Object.entries(filters).reduce((selectedFilters, [poiFilterId, poiFilterValue]) =>
		[...selectedFilters, ...extractSelectedFilterValues(poiFilterId as PoiFilterId, poiFilterValue, poiCategories)], [] as PoiFilterValue[]).flat();
}

export function isOnlyMichelinSelection(searchParams: SearchParams) {
	return !!(
		searchParams?.filters?.MICHELIN_ACCOMMODATION
		&& searchParams?.filters?.ONLY_MICHELIN_GUIDE_SELECTION
	);
}

export function areCategoriesWithMichelinPois(poiCategories: PoiCategory[]) {
	return (
		poiCategories.includes('ACCOMMODATION')
		|| poiCategories.includes('RESTAURANT')
		|| poiCategories.includes('TOURISM')
	);
}
